"use client"

import { createContext, useContext } from "react"
import english from "@/translations/en/ui-controls.json"
import { englishUiComponentTranslators, type UiComponentTranslators, type UiComponentNamespace } from "@/lib/i18n/ui-component-translations"
import type { UiLocale } from "@/lib/i18n/locales"

export const UI_CONTROL_KEYS = Object.keys(english.UiControls).filter(key => key !== "showingRange") as (keyof UiControlLabels["labels"])[]

export type UiControlLabels = {
  labels: Omit<typeof english.UiControls, "showingRange">
  locale: UiLocale
  range: (from: number, to: number, total: number) => string
  error: (message: string) => string
  fieldError: (message: string) => string
  translations: UiComponentTranslators
}

/** Shared controls retain their existing English defaults outside the main app. */
export const UiControlLabelsContext = createContext<UiControlLabels>({
  labels: english.UiControls, locale: "en",
  error: message => message,
  fieldError: message => message,
  translations: englishUiComponentTranslators,
  range: (from, to, total) => english.UiControls.showingRange
    .replace("{from}", String(from)).replace("{to}", String(to)).replace("{total}", String(total)),
})

export function useUiControlLabels() {
  return useContext(UiControlLabelsContext)
}

export function useSharedUiTranslations<Namespace extends UiComponentNamespace>(namespace: Namespace): UiComponentTranslators[Namespace] {
  return useUiControlLabels().translations[namespace]
}
